<template>
  <div class="next-path">
    <div
      class="next-path__img"
      :style="`background-image: url(${$getPlaylistBackground(
        learningPath.id
      )})`"
    >
      <h2>
        Learning <br />
        Path
      </h2>
    </div>
    <div class="next-path__content">
      <div class="next-path__content__overview">
        <div
          v-if="learningPath.trailer_jwplayer_id"
          class="next-path__content__overview__video-wrapper"
        >
          <SampleVideo
            :videoId="learningPath.trailer_jwplayer_id"
            :autostart="false"
          />
        </div>
        <h5 class="next-path__content__overview__title">
          {{ learningPath.name }}
        </h5>
        <p
          class="next-path__content__overview__description"
          v-html="learningPath.description"
        ></p>
        <div v-if="progress" class="next-path__content__overview__progress">
          <div class="next-path__content__overview__progress__bar">
            <div
              class="next-path__content__overview__progress__bar__completed"
              :style="`width: ${progress}%`"
            />
          </div>
        </div>
      </div>
      <div class="next-path__content__right">
        <div class="next-path__content__right__tile-wrapper">
          <SummaryTile
            :learningAreaColor="learningAreaColor"
            :certificate="certificate"
            :summary="summary"
          />
        </div>
        <div class="next-path__content__right__buttons-wrapper">
          <button
            v-if="progress"
            class="btn btn--secondary"
            @click="resumePath"
          >
            Resume training
          </button>
          <router-link
            v-else
            class="btn btn--secondary"
            :to="$buildContentLink('path', learningPath)"
          >
            Go to Learning Path
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SampleVideo from '@/components/media/SampleVideo.vue'
import SummaryTile from '@/components/learning-path/overview/SummaryTile.vue'
import { getLearningAreaColor, getLearningAreaName } from '@/helpers'

export default {
  name: 'Tile',
  components: {
    SampleVideo,
    SummaryTile,
  },
  props: {
    learningPath: {
      type: Object,
      required: true,
    },
    nextContent: {
      type: Object,
      required: false,
    },
  },
  computed: {
    ...mapState({
      certificationsCache: state => state.certifications.certificationsCache,
    }),
    certificate() {
      return this.certificationsCache[this.learningPath.certifications[0]] || {}
    },
    learningAreaName() {
      return getLearningAreaName(this.learningPath)
    },
    learningAreaColor() {
      return getLearningAreaColor(this.learningPath)
    },
    duration() {
      const { duration_in_seconds: duration } = this.learningPath
      return this.$duration.durationFromSecs(duration)
    },
    summary() {
      const { learningPath, certificate, duration } = this
      const { summary, difficulty } = learningPath

      return {
        DIFFICULTY: difficulty,
        DURATION: duration,
        SECTIONS: summary.total_groups,
        COURSES: summary.total_courses,
        VIDEOS: summary.total_videos,
        QUIZZES: summary.total_quizzes,
        LABS: summary.total_labs,
        CERTIFICATION: certificate.short_name,
      }
    },
    progress() {
      const progress = this.learningPath.user_status?.progress

      if (progress) return progress === 1 ? 100 : parseInt(progress * 100)

      return 0
    },
  },
  methods: {
    resumePath() {
      const { nextContent, learningAreaName } = this
      if (this.nextContent) {
        const { course, content, video } = nextContent
        const next = content || video
        switch (content.content_type) {
          case 'iframe':
            this.$store.dispatch('iframes/setPlayingIFrameId', next.id)
            break
          case 'lab':
            this.$store.dispatch('labs/setFocusLabId', next.id)
            break
          default:
            this.$store.dispatch('content/setPlayingVideoId', next.id)
            this.$store.dispatch('content/setPlayingVideoTime', 0)
            break
        }
        this.$router.push(this.$buildContentLink('course', course))
      } else {
        const {
          learningPath: { content },
        } = this
        const firstCourse = content[0].content[0]
        const { uuid: id, slug } = firstCourse
        this.$router.push(
          this.$buildContentLink('course', {
            id,
            slug,
            area: [{ name: learningAreaName }],
          })
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.next-path {
  display: flex;
  width: 100%;
  height: auto;
  background: var(--glass-mid-gray);
  overflow: hidden;
  border-radius: 10px;
  flex-flow: row wrap;

  @include desktop {
    flex-flow: row nowrap;
  }
  &__img {
    background-size: cover;
    padding: 30px;
    width: 100%;

    @include desktop {
      width: 310px;
    }

    h2 {
      margin: 0;

      font-family: var(--font-medium);
      font-size: rem(18px);
      line-height: rem(24px);

      @include mobile {
        font-family: var(--font-bold);
        font-size: rem(24px);
        line-height: rem(32px);
      }
      @include desktop {
        font-size: rem(40px);
        line-height: rem(52px);
      }

      br {
        display: none;

        @include desktop {
          display: initial;
        }
      }
    }
  }
  &__content {
    display: flex;
    flex-flow: column;
    padding: 40px 30px;

    @include mobile {
      gap: 20px;
      flex-flow: row;
    }
    @include mobile {
      gap: 50px;
    }

    &__overview {
      flex: 1;
      display: flex;
      flex-direction: column;

      &__video-wrapper {
        display: none;

        @include desktop {
          display: initial;
          margin-bottom: 30px;
        }
      }

      &__title {
        margin: 0;
        font-family: var(--font-medium);
        font-size: rem(16px);
        line-height: rem(23px);

        @include mobile {
          font-family: var(--font-bold);
          font-size: rem(20px);
          line-height: rem(26px);
        }
      }

      &__description {
        margin: 10px 0;
        font-family: var(--font-book);
        font-size: rem(16px);
        line-height: rem(24px);
        color: var(--light-blue-gray);

        @include mobile {
          margin: 20px 0;
        }

        ::v-deep a {
          color: var(--white);
          text-decoration: underline;
          &:visited {
            color: var(--white);
          }
        }
      }
      &__progress {
        margin-bottom: 20px;

        @include desktop {
          display: flex;
          height: 100%;
          align-items: flex-end;
        }

        &__bar {
          width: 100%;
          height: 8px;
          background-color: var(--mid-gray-dark-25);
          border-radius: 10px;

          &__completed {
            height: inherit;
            background-color: var(--white);
            border-radius: 10px;
            cursor: pointer;
          }
        }
      }
    }
    &__right {
      @include mobile {
        width: 250px;
      }
      @include desktop {
        width: 295px;
      }

      &__tile-wrapper {
        display: none;

        @include mobile {
          display: initial;
        }
      }

      &__buttons-wrapper {
        margin-top: 20px;

        @include mobile {
          display: flex;
          justify-content: flex-end;
          margin-top: 30px;
        }

        a,
        button {
          width: 100%;

          @include mobile {
            width: initial;
          }
        }
      }
    }
  }
}
</style>
